import React, { useCallback, useMemo, useState } from 'react';
import { CostReportItem } from '../../../../features/cost-report/types';
import _ from 'lodash';
import styles from './grid.module.scss';
import TimiDataGrid from '../../../../components/shared/grid';
import { IntlShape } from 'react-intl';
import { ExpandedGroupsRef, GridRef } from '../../../../components/shared/grid/types';
import { Lx } from '../../../../i18n/consts';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { createColumns, getAmountColumnWidth } from './helpers/columns';
import { createGroupBy } from './helpers/group-by';
import { buildTemplateColumns, createHeaderCells } from './helpers/headers';

interface CostReportGridProps {
    data: CostReportItem[];
    loading?: boolean;
    intl: IntlShape;
    onCreateCostClick: (cost: CostReportItem) => void;
    onEditCostClick: (cost: CostReportItem) => void;
    onDeleteCostClick: (cost: CostReportItem) => void;
    canManageCost: (cost: CostReportItem) => boolean;
    /* Group keys to re-open after returning from the cost form. Stable per mount. */
    initialExpandedGroupKeys?: Array<string>;
}

type CostReportGridRow = CostReportItem & { employeeGroupKey: string };
type EmployeeGroupMeta = { name: string; hasValues: boolean };

const EMPLOYEE_GROUP_KEY_DELIMITER = '__';

const CostReportGrid = (props: CostReportGridProps, ref: React.Ref<ExpandedGroupsRef>) => {
    const {
        data,
        loading = false,
        intl,
        onCreateCostClick,
        onEditCostClick,
        onDeleteCostClick,
        canManageCost,
    } = props;
    const isMobile = useMediaQuery({ maxWidth: 1000 });

    const columns = useMemo(() => 
        createColumns({ onAddNewCostClick: onCreateCostClick, onEditCostClick, onDeleteCostClick, canManageCost, isMobile }),
    [onCreateCostClick, onEditCostClick, onDeleteCostClick, canManageCost, isMobile]);

    const groupBy = useMemo(() => 
        createGroupBy({ onAddNewCostClick: onCreateCostClick, canManageCost, amountColumnWidth: getAmountColumnWidth(columns), groupField: 'employeeGroupKey' }),
    [onCreateCostClick, canManageCost, columns]);

    const gridRef = React.useRef<GridRef>(null);
    const [isAnyRowExpanded, setIsAnyRowExpanded] = useState(false);

    // Read on demand so the page need not mirror what is open. Reports nothing while the grid
    // is still loading and therefore unmounted.
    React.useImperativeHandle(ref, () => ({
        getExpandedGroupKeys: () => gridRef.current?.getExpandedRowKeys() ?? []
    }), []);

    const orderedItems = useMemo(() => _.orderBy(
        data,
        [
            (item) => item.amount === 0,
            (item) => item.employeeName.toLowerCase(),
            (item) => item.date,
        ],
        ['asc', 'asc', 'asc']
    ), [data]);

    const employeeGroupMeta = useMemo(() => {
        const meta = new Map<number, EmployeeGroupMeta>();
        orderedItems.forEach((item) => {
            const existing = meta.get(item.employeeId);
            if (!existing) {
                meta.set(item.employeeId, { name: item.employeeName, hasValues: item.id !== -1 });
                return;
            }

            if (!existing.hasValues && item.id !== -1) {
                meta.set(item.employeeId, { ...existing, hasValues: true });
            }
        });

        return meta;
    }, [orderedItems]);

    const resolveGroupKey = useCallback((item: CostReportItem) => {
        const meta = employeeGroupMeta.get(item.employeeId);
        const hasValues = (meta?.hasValues ?? item.id !== -1) ? '0' : '1';
        const employeeName = (meta?.name ?? item.employeeName).toLowerCase();
        return `${hasValues}${EMPLOYEE_GROUP_KEY_DELIMITER}${employeeName}${EMPLOYEE_GROUP_KEY_DELIMITER}${item.employeeId}`;
    }, [employeeGroupMeta]);

    const rows = useMemo<CostReportGridRow[]>(() => {
        return orderedItems.map((item) => ({
            ...item,
            employeeGroupKey: resolveGroupKey(item),
        }));
    }, [orderedItems, resolveGroupKey]);


    const visibleColumns = useMemo(
        () => columns.filter((column) => column.visible === undefined || column.visible),
        [columns]
    );

    const headerTemplateColumns = useMemo(() => buildTemplateColumns(columns), [columns]);

    const handleCollapseToggle = useCallback(() => {
        gridRef.current?.collapseRows();
    }, [gridRef]);

    const collapseLabel = isAnyRowExpanded
        ? intl.formatMessage({ id: Lx.General.COLLAPSE_ALL })
        : intl.formatMessage({ id: Lx.General.EXPAND_ALL });

    const headerCells = useMemo(
        () =>
            createHeaderCells({
                visibleColumns,
                intl,
                collapseLabel,
                isAnyRowExpanded,
                onCollapseToggle: handleCollapseToggle,
            }),
        [visibleColumns, intl, collapseLabel, isAnyRowExpanded, handleCollapseToggle]
    );

    if (loading) {
        return null;
    }

    return (
        <div className={classNames(styles.gridWrapper, styles.grid)}>
            {isMobile
                ? null
                :
                <div className={styles.customHeaderWrapper}>
                    <div className={styles.customHeader} style={headerTemplateColumns}>
                        {headerCells}
                    </div>
                </div> }
            <TimiDataGrid
                hideHeader 
                columns={columns} 
                rows={rows} 
                groupBy={groupBy}
                intl={intl}
                className={classNames(styles.grid, { [styles.mobileGrid]: isMobile })}
                autoExpandSingleGroup
                initialExpandedRowKeys={props.initialExpandedGroupKeys}
                ref={gridRef}
                onAnyRowExpand={setIsAnyRowExpanded} />
        </div>
    )
}

export default React.forwardRef(CostReportGrid) as (
    props: CostReportGridProps & React.RefAttributes<ExpandedGroupsRef>
) => React.JSX.Element;
